/* eslint-disable max-lines */
import { getService } from 'api/Service';

import { iaRa, inRn } from 'lib/helpers/utility';

import {
  calculateAmountFromPercentage,
  calculatePercentageFromAmount
} from '../../InvoiceFooter/InvoiceSummary/lib/helpers';

const NO_VALUE = undefined;

export const calculateInvoiceTaxFromItems = ({
  products,
  procedures,
  packages,
  mergedItems
} = {}) => {
  let taxAmount = 0;

  products?.forEach((item) => {
    if (!item) return;

    const itemAmount = Math.round(item.total_amount_cents || 0);
    const percent = ((item?.local_tax_rate || 0) + (item?.state_tax_rate || 0)) / 10000;

    const netAmount = Math.round(itemAmount - (item?.discount?.amount_cents || 0));
    taxAmount += Math.round(netAmount * percent);
  });

  [...(procedures || []), ...(mergedItems || [])]?.forEach((item) => {
    if (!item) return;

    if (item?.products?.length || item?.procedures?.length) {
      taxAmount += calculateInvoiceTaxFromItems({ packages: [item] });
      return;
    }

    const qty =
      !item?.type || item?.type?.toLowerCase() === 'product'
        ? 1
        : (item?.quantity ?? item?.units ?? item?.sales_count) || 1;

    const itemAmount = Math.round(
      item?.pt_balance ?? item?.total_cost_cents ?? item?.total_amount_cents ?? 0
    );

    const discountCents = item?.discount?.amount_cents || 0;

    const netAmount = Math.round(itemAmount * qty - discountCents);

    const percent = ((item?.local_tax_rate || 0) + (item?.state_tax_rate || 0)) / 10000;

    taxAmount += Math.round(netAmount * percent);
  });

  packages?.forEach((item) => {
    if (item?.products?.length || item?.procedures?.length) {
      taxAmount += calculateInvoiceTaxFromItems(item);
    }
  });

  return Math.round(taxAmount);
};

export const calculateInvoiceDiscountFromItems = ({ products, procedures, packages } = {}) => {
  let discountAmount = 0;

  iaRa(products)?.forEach((item) => {
    if (!item) return;
    discountAmount += inRn(item?.discount?.amount_cents);
  });

  iaRa(procedures)?.forEach((item) => {
    if (!item) return;
    discountAmount += inRn(item?.discount?.amount_cents);
  });

  iaRa(packages)?.forEach((item) => {
    if (!item) return;
    discountAmount += inRn(item?.discount?.amount_cents);
  });

  return discountAmount;
};

const resetFields = (fields, overrides = {}) => {
  const resetValues = Object.fromEntries(fields.map((f) => [f, NO_VALUE]));
  return { ...resetValues, ...overrides };
};

const handleDiscountChange = (procedure, value, tempId, type) => {
  if (procedure?.tempId !== tempId) return procedure;

  if (type === 'amount') {
    const amountCents = inRn(value.amount_cents);
    const totalCostCents = inRn(procedure.total_cost_cents);
    const percentage = calculatePercentageFromAmount(amountCents, totalCostCents);

    return {
      ...procedure,
      discount: { ...procedure.discount, amount_cents: amountCents, percentage }
    };
  }

  if (type === 'percent') {
    const percentageValue = inRn(value.percentage);
    const totalCostCents = inRn(procedure.total_cost_cents);
    const totalAmountFromPercentage = calculateAmountFromPercentage(
      percentageValue,
      totalCostCents
    );

    return {
      ...procedure,
      discount: {
        ...procedure.discount,
        amount_cents: totalAmountFromPercentage,
        percentage: percentageValue
      }
    };
  }

  return procedure;
};

const handleDiscountUnitChange = (procedure, value, tempId, discountType) => {
  const currentDiscount = procedure?.discount || {};
  const currentAmountCents = inRn(currentDiscount?.amount_cents);
  const currentPercentage = inRn(currentDiscount?.percentage);

  const totalForConversion = inRn(procedure?.total_cost_cents || procedure?.total_amount_cents);

  if (procedure?.tempId !== tempId) return procedure;

  let newAmountCents = 0;
  let newPercentage = 0;

  if (discountType === 'amount') {
    newAmountCents = calculateAmountFromPercentage(currentPercentage, totalForConversion);
    newPercentage = calculatePercentageFromAmount(newAmountCents, totalForConversion);
  } else {
    newPercentage = calculatePercentageFromAmount(currentAmountCents, totalForConversion);
    newAmountCents = calculateAmountFromPercentage(newPercentage, totalForConversion);
  }

  return {
    ...procedure,
    discount: {
      ...currentDiscount,
      amount_cents: newAmountCents,
      percentage: newPercentage,
      type: procedure.discount.type
    }
  };
};

export const handleChangePriceProcedures = (procedure, newValue, tempId, key) => {
  if (procedure.tempId !== tempId) return procedure;

  const price = inRn(newValue);
  const { pt_balance, total_cost_cents } = (procedure = {});
  const updated = { ...procedure };

  if (key === 'charge') {
    return { ...updated, charge: price };
  }

  if (key === 'ins_payment') {
    return { ...updated, ins_payment: price, allowed: price };
  }

  const isDifferentFromBalance =
    (pt_balance && price !== pt_balance) || (!pt_balance && price !== total_cost_cents);

  if (isDifferentFromBalance) {
    updated.pt_balance = price;
    updated.modified = true;

    if (price === total_cost_cents) {
      delete updated.pt_balance;
      delete updated.modified;
    }
  } else if (pt_balance === total_cost_cents) {
    delete updated.pt_balance;
    delete updated.modified;
  }

  return updated;
};

export const handleProcedureType = async (procedure, type, serviceId) => {
  const { service } = type !== 'unapplied' ? await getService(() => {}, { serviceId }) : {};

  const typeHandlers = {
    self_pay: {
      ...procedure,
      ...resetFields(['pt_balance', 'modified', 'ins_payment', 'allowed']),
      type,
      total_cost_cents: service?.total_cost_cents ?? service?.charge,
      tempId: procedure.tempId
    },

    primary: {
      ...procedure,
      ...resetFields(['pt_balance', 'modified', 'total_cost_cents']),
      type,
      charge: service?.standard_charge ?? 0,
      ins_payment: service?.total_cost_cents,
      tempId: procedure.tempId
    },

    unapplied: {
      ...procedure,
      ...resetFields([
        'pt_balance',
        'modified',
        'total_cost_cents',
        'ins_payment',
        'allowed',
        'charge'
      ]),
      type,
      tempId: procedure.tempId
    }
  };

  return typeHandlers[type] ?? { ...procedure, type };
};

const applyTaxChange = (item, field) => ({
  ...item,
  [field]: inRn(item[field])
});

const applyDiscountChange = (item, field) => {
  const discountType = item.discount?.discount_type;

  return discountType === 'unit'
    ? handleDiscountUnitChange(item, item[field], item.tempId, item.discount.type)
    : handleDiscountChange(item, item[field], item.tempId, item.discount.type);
};

const updateProductOrPackage = (items, item, field) =>
  iaRa(items).map((currentItem) => {
    if (currentItem.tempId !== item.tempId) return currentItem;

    if (field === 'local_tax_rate' || field === 'state_tax_rate') {
      return applyTaxChange(item, field);
    }

    if (field === 'discount') {
      return applyDiscountChange(item, field);
    }

    if (field === 'sales_count') {
      return {
        ...currentItem,
        sales_count: item.sales_count,
        total_amount_cents: item.amount_cents * item.sales_count
      };
    }

    return { ...currentItem, ...item };
  });

export const updateInvoiceData = async (colDef, editedRowData, editedInvoice) => {
  const { field } = colDef;

  const updatedInvoice = { ...editedInvoice };

  if (editedRowData?.type === 'Product') {
    updatedInvoice.products = updateProductOrPackage(editedInvoice.products, editedRowData, field);
  } else if (editedRowData?.type === 'Package') {
    updatedInvoice.packages = updateProductOrPackage(editedInvoice.packages, editedRowData, field);
  } else {
    updatedInvoice.procedures = await Promise.all(
      iaRa(editedInvoice.procedures).map(async (proc) => {
        if (proc.tempId !== editedRowData.tempId) return proc;

        if (field === 'type') {
          return handleProcedureType(editedRowData, editedRowData.type, editedRowData.id);
        }

        if (field === 'local_tax_rate' || field === 'state_tax_rate') {
          return applyTaxChange(editedRowData, field);
        }

        if (field === 'discount') {
          return applyDiscountChange(editedRowData, field);
        }

        const priceUpdated = handleChangePriceProcedures(
          editedRowData,
          editedRowData[field],
          editedRowData.tempId,
          field
        );

        return { ...editedRowData, ...priceUpdated };
      })
    );
  }

  const taxCents = calculateInvoiceTaxFromItems(updatedInvoice);
  const discountCents = calculateInvoiceDiscountFromItems(updatedInvoice);

  return {
    ...updatedInvoice,
    tax: taxCents,
    discount: discountCents
  };
};
